import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 20;

function EmailTemplates({ token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [successMsg] = useState(location.state?.saved ? 'Plantilla guardada correctamente.' : '');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/email-templates?page=${page}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await fetch(`/api/email-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Plantillas de correo</h1>
          <button
            onClick={() => navigate('/plantillas-email/nueva')}
            style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
          >
            + Crear
          </button>
        </div>

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', color: theme.colors.success, fontSize: '0.88rem', marginBottom: '1.5rem' }}>
            {successMsg}
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  {['Nombre', 'Creado', 'Acciones'].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>Sin plantillas. Crea la primera.</td></tr>
                ) : data.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '9px 12px', color: theme.colors.text, fontWeight: '500' }}>{row.nombre}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleDateString('es-CO')}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => navigate(`/plantillas-email/${row.id}/editar`)}
                          style={{ padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '6px', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.8rem' }}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(row.id)}
                          disabled={deletingId === row.id}
                          style={{ padding: '5px 12px', backgroundColor: confirmDeleteId === row.id ? theme.colors.error : 'transparent', border: `1px solid ${theme.colors.error}`, borderRadius: '6px', color: confirmDeleteId === row.id ? '#fff' : theme.colors.error, cursor: deletingId === row.id ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}
                        >
                          {confirmDeleteId === row.id ? '¿Confirmar?' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === 1 ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === totalPages ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>Siguiente →</button>
        </div>
      </div>
    </main>
  );
}

export default EmailTemplates;
